/**
 * The client profile's field grouping — the legacy's five fieldsets, minus the
 * ones we cannot yet fill.
 *
 * Kept pure and separate from the screen for one reason: **FR-C3's rule that a
 * field we do not hold is absent, never blank-but-present.** An empty box says
 * "there is a value here and it is unknown", which is the same lie a defaulted
 * number tells. That rule is worth a test, and a test wants a function.
 */

import type { ClientRecord, ClientUpdate } from './api'
import { registrationTypeLabel } from './portalLookup'

export interface ProfileField {
  key: string
  label: string
  value: string
  /** Rendered in the tabular figures face — codes, not prose. */
  mono?: boolean
}

export interface ProfileGroup {
  title: string
  fields: ProfileField[]
}

/** Present means: the record carries it and it is not an empty string. */
function present(value: unknown): value is string | number {
  if (value === null || value === undefined) return false
  return String(value).trim() !== ''
}

function group(title: string, fields: Array<ProfileField | null>): ProfileGroup | null {
  const kept = fields.filter((f): f is ProfileField => f !== null)
  // A group whose every field is absent is itself absent — an empty fieldset
  // is a promise of content.
  return kept.length ? { title, fields: kept } : null
}

function field(
  key: string,
  label: string,
  value: unknown,
  opts: { mono?: boolean } = {},
): ProfileField | null {
  return present(value) ? { key, label, value: String(value), ...opts } : null
}

/** A client's status in words (N29), never the stored `active` / `archived`. */
export const CLIENT_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  archived: 'Archived',
}

export function clientStatusLabel(status: string | null | undefined): string | null {
  if (!status) return null
  return CLIENT_STATUS_LABEL[status] ?? 'A status ProGST has no name for'
}

/**
 * The groups to render, in the legacy's order.
 *
 * A group appears only once the record carries at least one of its fields, so a
 * client onboarded with nothing but a GSTIN shows Identity and Company details
 * and no empty promises. The **authorized signatory** is the one that matters
 * beyond tidiness: the GSTN filing payload requires it, so its absence here is
 * a real gap in the record rather than a blank box.
 */
export function profileGroups(client: ClientRecord): ProfileGroup[] {
  return [
    group('Identity', [
      field('gstin', 'GSTIN', client.gstin, { mono: true }),
      field('pan', 'PAN', client.pan, { mono: true }),
      field('state_code', 'State code', client.state_code, { mono: true }),
      field('status', 'Status', clientStatusLabel(client.status)),
    ]),
    group('Company details', [
      field('legal_name', 'Legal name', client.legal_name),
      field('trade_name', 'Trade name', client.trade_name),
      field('registration_type', 'Registration type', registrationTypeLabel(client.registration_type)),
      field('constitution', 'Constitution', client.constitution),
    ]),
    group('Registered address', [
      field('address_line', 'Address', client.address_line),
      field('city', 'City', client.city),
      field('pincode', 'Pincode', client.pincode, { mono: true }),
    ]),
    group('Contact', [
      field('contact_person', 'Contact person', client.contact_person),
      field('contact_phone', 'Phone', client.contact_phone, { mono: true }),
      field('contact_email', 'Email', client.contact_email),
    ]),
    group('Authorized signatory', [
      field('signatory_name', 'Name', client.signatory_name),
      field('signatory_designation', 'Designation', client.signatory_designation),
    ]),
  ].filter((g): g is ProfileGroup => g !== null)
}

/** The fields the service's `ClientPatch` accepts — GSTIN, PAN, state and
 *  status are not among them. */
export const EDITABLE = [
  'legal_name',
  'trade_name',
  'registration_type',
  'constitution',
  'address_line',
  'city',
  'pincode',
  'contact_person',
  'contact_phone',
  'contact_email',
  'signatory_name',
  'signatory_designation',
] as const

export interface EditField {
  key: keyof ClientUpdate
  label: string
}

/**
 * The edit form, in the same groups the read view uses — the legacy's
 * fieldsets. Driven by data rather than written out as markup so the two
 * cannot drift: a field added to one and forgotten in the other is a value the
 * screen shows and refuses to change, or accepts and never displays.
 */
export const EDIT_GROUPS: Array<{ title: string; fields: EditField[] }> = [
  {
    title: 'Company details',
    fields: [
      { key: 'legal_name', label: 'Legal name' },
      { key: 'trade_name', label: 'Trade name' },
      { key: 'registration_type', label: 'Registration type' },
      { key: 'constitution', label: 'Constitution' },
    ],
  },
  {
    title: 'Registered address',
    fields: [
      { key: 'address_line', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'pincode', label: 'Pincode' },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { key: 'contact_person', label: 'Contact person' },
      { key: 'contact_phone', label: 'Phone' },
      { key: 'contact_email', label: 'Email' },
    ],
  },
  {
    title: 'Authorized signatory',
    fields: [
      { key: 'signatory_name', label: 'Name' },
      { key: 'signatory_designation', label: 'Designation' },
    ],
  },
]

/**
 * A form draft as the API wants it: an emptied optional field is `null`, never
 * `""`. The columns are nullable, and a blank string is a value nobody typed —
 * it would read back as "there is a contact and their name is nothing".
 */
export function toPatch(draft: Record<string, string>): ClientUpdate {
  const out: Record<string, string | null> = {}
  for (const [key, value] of Object.entries(draft)) {
    const trimmed = value.trim()
    out[key] = trimmed === "" ? null : trimmed
  }
  return out as ClientUpdate
}
